import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Building2, FileText, Package, Trash2, ArrowRight, X, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

const CONFLICT_LABELS = {
  razao_social: "Razão Social",
  cnpj: "CNPJ",
  ie: "Inscrição Estadual",
  municipio: "Município",
  uf: "UF",
  logradouro: "Logradouro",
  numero: "Número",
  cep: "CEP",
  telefone: "Telefone",
};

const CAMPOS = [
  { name: "razao_social", label: "Razão Social", ph: "COMÉRCIO EXEMPLO LTDA", col: 2, req: true },
  { name: "cnpj", label: "CNPJ", ph: "00.000.000/0001-00", req: true },
  { name: "ie", label: "Inscrição Estadual", ph: "0000000000000", req: true },
  { name: "municipio", label: "Município", ph: "BELO HORIZONTE", req: true },
  { name: "uf", label: "UF", ph: "MG" },
  { name: "logradouro", label: "Logradouro", ph: "RUA DAS FLORES" },
  { name: "numero", label: "Número", ph: "100" },
  { name: "bairro", label: "Bairro", ph: "CENTRO" },
  { name: "cep", label: "CEP", ph: "30000-000" },
  { name: "contato", label: "Contato", ph: "JOÃO SILVA" },
  { name: "telefone", label: "Telefone", ph: "31999998888" },
];

function NovaEmpresaModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ uf: "MG" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    for (const c of CAMPOS.filter((x) => x.req)) {
      if (!form[c.name]) return toast.error(`Preencha ${c.label}`);
    }
    setSaving(true);
    try {
      const emp = await api.criarEmpresa(form);
      toast.success("Empresa cadastrada!");
      onCreated(emp);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="nova-empresa-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-display font-bold text-xl">Nova Empresa</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="fechar-modal-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          {CAMPOS.map((c) => (
            <div key={c.name} className={c.col === 2 ? "col-span-2" : ""}>
              <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wide">
                {c.label} {c.req && <span className="text-orange-500">*</span>}
              </label>
              <input
                data-testid={`campo-${c.name}`}
                value={form[c.name] || ""}
                onChange={(e) => setForm({ ...form, [c.name]: e.target.value })}
                placeholder={c.ph}
                className="w-full px-3.5 py-2.5 rounded-lg border border-zinc-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition text-sm"
              />
            </div>
          ))}
          <div className="col-span-2 flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-lg border border-zinc-200 font-semibold text-zinc-600 hover:bg-zinc-50 transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="salvar-empresa-btn"
              className="flex-1 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Cadastrar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function Dashboard() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncConflicts, setSyncConflicts] = useState(null);
  const nav = useNavigate();

  const carregar = async () => {
    try {
      setEmpresas(await api.listarEmpresas());
    } catch {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const sincronizarPdv = async () => {
    setSyncing(true);

    try {
      const resultado = await api.sincronizarCadastroPdv();
      const conflitos = Object.keys(resultado?.conflitos || {});

      if (conflitos.length > 0) {
        setSyncConflicts({
          empresa_id: resultado?.empresa_id,
          conflitos: resultado?.conflitos || {},
        });

        toast.warning(
          `Foram encontradas ${conflitos.length} divergência(s) de cadastro.`
        );
      } else {
        setSyncConflicts(null);
      }

      if (conflitos.length === 0 && resultado?.status === "criado") {
        toast.success("Empresa do PDV adicionada ao SINTEGRA.");
      } else if (conflitos.length === 0 && resultado?.status === "atualizado") {
        toast.success("Cadastro atualizado com os dados do PDV.");
      } else if (conflitos.length === 0) {
        toast.success("Cadastro do PDV já está atualizado.");
      }

      await carregar();
    } catch (err) {
      const mensagem =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Não foi possível sincronizar o cadastro do PDV.";

      toast.error(mensagem);
    } finally {
      setSyncing(false);
    }
  };

  const excluir = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Excluir empresa e todos os dados?")) return;
    await api.excluirEmpresa(id);
    toast.success("Empresa excluída");
    carregar();
  };

  return (
    <Layout>
      <div className="fade-up">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 text-orange-600 text-xs font-semibold mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Área do Estabelecimento
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900">
              Suas empresas
            </h1>
            <p className="text-zinc-500 mt-2 max-w-lg">
              Cadastre estabelecimentos, receba dados do ERP via API e gere o arquivo SINTEGRA para o contador baixar sozinho.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={sincronizarPdv}
              disabled={syncing}
              data-testid="sincronizar-pdv-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white font-semibold transition-colors disabled:opacity-60"
            >
              {syncing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              {syncing ? "Sincronizando..." : "Sincronizar cadastro do PDV"}
            </button>

            <button
              onClick={() => setModal(true)}
              data-testid="nova-empresa-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1c1c1f] hover:bg-orange-500 text-white font-semibold transition-colors shadow-lg"
            >
              <Plus className="w-5 h-5" /> Nova empresa
            </button>
          </div>
        </div>

        <AnimatePresence>
          {syncConflicts &&
            Object.keys(syncConflicts.conflitos || {}).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5"
                data-testid="sync-conflicts-panel"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                  </div>

                  <div className="flex-1">
                    <h2 className="font-display font-bold text-lg text-zinc-900">
                      Divergências de cadastro
                    </h2>
                    <p className="text-sm text-zinc-600 mt-1">
                      O PDV e o SINTEGRA possuem valores diferentes nos campos abaixo.
                      Nenhum valor divergente foi sobrescrito automaticamente.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSyncConflicts(null)}
                    className="text-zinc-400 hover:text-zinc-700 p-1"
                    aria-label="Fechar divergências"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {Object.entries(syncConflicts.conflitos).map(
                    ([campo, valores]) => (
                      <div
                        key={campo}
                        className="bg-white rounded-xl border border-amber-100 p-4"
                      >
                        <div className="font-semibold text-sm text-zinc-800 mb-3">
                          {CONFLICT_LABELS[campo] || campo}
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="rounded-lg bg-zinc-50 p-3">
                            <div className="text-[11px] uppercase tracking-wide font-bold text-zinc-400 mb-1">
                              PDV
                            </div>
                            <div className="text-sm font-medium text-zinc-800 break-words">
                              {String(valores?.pdv ?? "") || "—"}
                            </div>
                          </div>

                          <div className="rounded-lg bg-zinc-50 p-3">
                            <div className="text-[11px] uppercase tracking-wide font-bold text-zinc-400 mb-1">
                              SINTEGRA
                            </div>
                            <div className="text-sm font-medium text-zinc-800 break-words">
                              {String(valores?.sintegra ?? "") || "—"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <p className="text-xs text-amber-800 mt-4">
                  Revise os dados antes de decidir qual informação deve prevalecer.
                  A sincronização continuará preservando ambos os cadastros até a correção.
                </p>
              </motion.div>
            )}
        </AnimatePresence>

        {loading ? (
          <div className="flex justify-center py-24 text-zinc-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : empresas.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-200 rounded-2xl py-20 text-center" data-testid="empty-state">
            <Building2 className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
            <p className="font-display font-semibold text-lg text-zinc-700">Nenhuma empresa ainda</p>
            <p className="text-zinc-400 mt-1">Clique em "Nova empresa" para começar.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="empresas-grid">
            {empresas.map((e, i) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => nav(`/empresa/${e.id}`)}
                data-testid={`empresa-card-${e.id}`}
                className="group bg-white rounded-2xl p-5 border border-zinc-100 hover:border-orange-300 hover:shadow-xl hover:shadow-orange-500/5 cursor-pointer transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-600 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <button
                    onClick={(ev) => excluir(ev, e.id)}
                    data-testid={`excluir-empresa-${e.id}`}
                    className="text-zinc-300 hover:text-red-500 transition p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="font-display font-bold text-lg mt-4 text-zinc-900 line-clamp-1">{e.razao_social}</h3>
                <p className="text-zinc-400 text-sm font-mono mt-0.5">{e.cnpj}</p>
                <div className="flex gap-4 mt-4 pt-4 border-t border-zinc-100 text-xs text-zinc-500">
                  <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {e.total_notas} notas</span>
                  <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> {e.total_produtos} prod.</span>
                  <span className="ml-auto flex items-center gap-1 text-orange-600 font-semibold group-hover:gap-2 transition-all">
                    Abrir <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {modal && (
          <NovaEmpresaModal
            onClose={() => setModal(false)}
            onCreated={(emp) => {
              setModal(false);
              nav(`/empresa/${emp.id}`);
            }}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
